import { posts, ranking } from "@/mocks/content";
import SectionHeading from "@/components/feature/SectionHeading";
import { catOf } from "./helpers";

export default function Hot() {
  const long = posts.find((p) => p.id === "p05") ?? posts[0];
  const trendIcon = (t: "up" | "down" | "new") => {
    if (t === "new") return { icon: "ri-sparkling-2-fill", cls: "text-primary-600" };
    if (t === "up") return { icon: "ri-arrow-up-line", cls: "text-accent-600" };
    return { icon: "ri-arrow-down-line", cls: "text-foreground-400" };
  };

  return (
    <section id="hot" className="w-full bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="编辑部观察"
          title="深度长文 & 实时热榜"
          desc="一边是编辑部与资深吃瓜人写下的长文复盘，一边是此刻最热的瓜讯排名，两条线互为补充。"
        />

        <div className="mt-8 grid gap-8 lg:grid-cols-5">
          {/* long read */}
          <div className="lg:col-span-3">
            <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100 sm:flex-row">
              <a href="#guide" className="block shrink-0 sm:w-2/5" aria-label={long.title}>
                <img
                  src={long.img}
                  alt={long.title}
                  title={`${long.title} - 每日大瓜长文`}
                  className="h-56 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 sm:h-full"
                />
              </a>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-700">编辑部特稿</span>
                  <span className="text-xs text-foreground-500">{catOf(long).name} · {long.date}</span>
                </div>
                <h3 className="mt-3 font-heading text-xl font-black leading-snug text-foreground-950 group-hover:text-primary-700 md:text-2xl">
                  <a href="#guide" className="transition-colors">{long.title}</a>
                </h3>
                <p className="lead-first mt-4 text-sm leading-relaxed text-foreground-600">{long.summary}</p>
                <div className="mt-auto flex items-center justify-between pt-5 text-sm">
                  <span className="flex items-center gap-2 text-foreground-500">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-edit-line" aria-hidden="true" />
                    </span>
                    大瓜编辑部
                  </span>
                  <a
                    href="#guide"
                    className="flex items-center gap-1 whitespace-nowrap font-medium text-primary-600 hover:text-primary-700"
                  >
                    读全文 <i className="ri-arrow-right-line" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </article>

            {/* editorial notes */}
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {[
                { icon: "ri-question-mark", t: "先求证再站队", d: "截图不等于实锤，让子弹飞一会儿" },
                { icon: "ri-chat-smile-3-line", t: "吐槽有分寸", d: "观点可辛辣，落点不伤人" },
                { icon: "ri-time-line", t: "关注当下与明天", d: "不沉迷挖坟，也拥抱新瓜" },
              ].map((n) => (
                <div key={n.t} className="rounded-xl border border-background-200 bg-background-100 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={n.icon} aria-hidden="true" />
                  </span>
                  <p className="mt-3 text-sm font-bold text-foreground-900">{n.t}</p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{n.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ranking */}
          <div className="lg:col-span-2">
            <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100">
              <div className="flex items-center justify-between border-b border-background-200 bg-primary-500 px-5 py-3.5">
                <h3 className="flex items-center gap-2 font-heading text-lg font-black text-background-50">
                  <i className="ri-fire-fill" aria-hidden="true" /> 今日热榜 TOP 10
                </h3>
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs text-background-50">每小时刷新</span>
              </div>
              <ol className="flex-1 divide-y divide-background-200">
                {ranking.map((r) => {
                  const tr = trendIcon(r.trend);
                  return (
                    <li key={r.rank}>
                      <a href="#columns" className="group flex items-center gap-3 px-5 py-3 transition-colors hover:bg-background-50">
                        <span
                          className={`w-6 shrink-0 text-center font-heading text-base font-black ${
                            r.rank <= 3 ? "text-primary-500" : "text-foreground-400"
                          }`}
                        >
                          {String(r.rank).padStart(2, "0")}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="line-clamp-1 text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                            {r.title}
                          </span>
                          <span className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                            <span className="rounded bg-background-200 px-1.5 py-px text-[11px] text-foreground-600">{r.cat}</span>
                            <span>{r.heat}</span>
                          </span>
                        </span>
                        <i className={`text-sm ${tr.icon} ${tr.cls}`} aria-hidden="true" />
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}